import { useEffect, useRef } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';

/**
 * Retains the patient-list filter / pagination state across navigation. The
 * list keeps all of its state in the URL query string (q, status, flag,
 * addedBy, clinics, sort, dir, limit, page); this persists that string and
 * restores it when the list is opened WITHOUT one — so returning from a
 * patient (via the rail's "All patients" link or the Patients nav) lands back
 * on the same filtered view instead of a cleared list.
 *
 * Per-session (sessionStorage): a fresh browser session starts clean, but the
 * filters survive any number of patient detours within the session. Clearing
 * the filters on the list clears the saved copy too, so a deliberate reset
 * sticks.
 */

const STORAGE_KEY = 'ui.patientListFilters';

export const readPatientListFilters = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
};

const writePatientListFilters = (qs: string) => {
  try {
    if (qs) sessionStorage.setItem(STORAGE_KEY, qs);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode / quota — ignore */
  }
};

export const useRetainPatientListFilters = (
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
): void => {
  // Snapshot the stored filters at first render, before the sync effect below
  // can overwrite them — makes the one-time restore independent of effect
  // ordering.
  const initialStored = useRef<string | null>();
  if (initialStored.current === undefined) initialStored.current = readPatientListFilters();

  // Restore exactly once, and only when the URL carries no params of its own
  // (so a bookmarked/back-button URL with explicit filters always wins).
  const didRestore = useRef(false);
  useEffect(() => {
    if (didRestore.current) return;
    didRestore.current = true;
    if (searchParams.toString() === '' && initialStored.current) {
      setSearchParams(new URLSearchParams(initialStored.current), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror the live URL into storage, including clears (empty → removed).
  const qs = searchParams.toString();
  useEffect(() => {
    writePatientListFilters(qs);
  }, [qs]);
};
